import { useEffect, useState, type ReactNode } from 'react';
import { CalendarClock, ClipboardList, Clock3, ExternalLink, Eye, FileText, Image as ImageIcon, Plus, X } from 'lucide-react';
import { useAtendimento, useAtendimentos, useExamesImagem } from '@/hooks/useAtendimentos';
import { usePatientClinicalEncounters } from '@/hooks/useConsultasClinicas';
import { usePaciente } from '@/hooks/usePaciente';
import { LoadingState } from '@/components/shared/LoadingState';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { api, apiUrl } from '@/lib/api';
import { classificarAtributo, formatarData } from '@/lib/utils';
import type { ClinusAtendimento, ClinusAtributo, ExameImagem } from '@/types/api';

export function PatientHistoryPageContent({ cdPaciente }: { cdPaciente: number }) {
  const { data, isLoading } = useAtendimentos(cdPaciente, { refetchInterval: false });
  const { data: pepItems, isLoading: pepLoading } = usePatientClinicalEncounters(cdPaciente);
  const [pepPreview, setPepPreview] = useState<NonNullable<typeof pepItems>[number] | null>(null);
  const [consultaPreview, setConsultaPreview] = useState<ClinusAtendimento | null>(null);
  const { data: detalhe, isFetching } = useAtendimento(consultaPreview?.cdAtendimento ?? null);
  const items = (data ?? []).filter((item) => {
    const p = (item.dsProcedimento ?? '').toUpperCase();
    return item.cdAtendimento > 0 && (p.includes('CONSULTA') || p.includes('RETORNO'));
  });

  return (
    <>
      <ClinicalShell title="Histórico PEP" eyebrow="Consultas realizadas no PEP">
        {pepLoading && <LoadingState />}
        {!pepLoading && (pepItems ?? []).length === 0 && <p className="text-sm text-muted-foreground">Nenhuma consulta realizada no PEP.</p>}
        <div className="relative max-h-[68vh] overflow-auto pl-8 pr-2 before:absolute before:left-[11px] before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-border">
          {(pepItems ?? []).map((item) => (
            <div key={item.id} className="group relative mb-3 rounded-lg border bg-background p-4 shadow-sm transition hover:border-primary/40 hover:bg-primary/[0.03] hover:shadow-md">
              <span className="absolute -left-[27px] top-5 flex h-6 w-6 items-center justify-center rounded-full border bg-primary/10 ring-4 ring-background"><ClipboardList className="h-3 w-3 text-primary" /></span>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">Consulta PEP</div>
                  <div className="mt-1 text-xs text-muted-foreground">{formatarData(item.startedAt)} · {item.medico?.login ?? 'Médico'} · Atendimento {item.cdAtendimento}</div>
                  <div className="mt-1 text-xs"><span className="rounded-full bg-primary/10 px-2 py-1 capitalize text-primary">{item.status.replace('_', ' ')}</span></div>
                </div>
                <Button className="shrink-0" variant="outline" size="sm" onClick={() => setPepPreview(item)}><Eye className="mr-2 h-4 w-4" />Visualizar</Button>
              </div>
            </div>
          ))}
        </div>
      </ClinicalShell>
      <ClinicalShell title="Histórico Clinux" eyebrow="Paciente">
        {isLoading && <LoadingState />}
        {!isLoading && items.length === 0 && <p className="text-sm text-muted-foreground">Nenhum histórico de consulta encontrado.</p>}
        <div className="relative max-h-[68vh] overflow-auto pl-8 pr-2 before:absolute before:left-[11px] before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-border">
          {items.map((item) => (
            <div key={item.cdAtendimento} className="group relative mb-3 rounded-lg border bg-background p-4 shadow-sm transition hover:border-primary/40 hover:bg-primary/[0.03] hover:shadow-md">
              <span className="absolute -left-[27px] top-5 flex h-6 w-6 items-center justify-center rounded-full border bg-primary/10 ring-4 ring-background">
                <CalendarClock className="h-3 w-3 text-primary" />
              </span>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{item.dsProcedimento ?? 'Consulta'}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{formatarData(item.dtData)} · {item.dsMedico ?? '-'}</div>
                </div>
                <Button className="shrink-0" variant="outline" size="sm" onClick={() => setConsultaPreview(item)}>
                  <Eye className="mr-2 h-4 w-4" />Visualizar
                </Button>
              </div>
            </div>
          ))}
        </div>
      </ClinicalShell>
      {consultaPreview && (
        <CenteredPreview
          title={consultaPreview.dsProcedimento ?? 'Consulta'}
          subtitle={`${formatarData(consultaPreview.dtData)} · Atendimento ${consultaPreview.cdAtendimento}`}
          onClose={() => setConsultaPreview(null)}
        >
          {isFetching && <LoadingState />}
          {!isFetching && getConsultaDocs(detalhe?.atributos ?? [], consultaPreview).length === 0 && (
            <p className="text-sm text-muted-foreground">Sem documento de consulta para este atendimento.</p>
          )}
          {!isFetching && getConsultaDocs(detalhe?.atributos ?? [], consultaPreview).slice(0, 1).map((doc) => (
            <iframe key={doc.cd_valor} title={doc.ds_atributo} src={`${apiUrl}/api/atendimentos/${consultaPreview.cdAtendimento}/atributos/${doc.cd_valor}/preview.pdf`} className="h-[68vh] w-full rounded-md border bg-muted/20" />
          ))}
        </CenteredPreview>
      )}
      {pepPreview && (
        <CenteredPreview title="Consulta PEP" subtitle={`${formatarData(pepPreview.startedAt)} · Atendimento ${pepPreview.cdAtendimento}`} onClose={() => setPepPreview(null)}>
          <div className="space-y-4">
            <div className="rounded-md bg-muted/40 p-3 text-sm"><strong>Médico:</strong> {pepPreview.medico?.login ?? '-'}<br /><strong>Status:</strong> <span className="capitalize">{pepPreview.status.replace('_', ' ')}</span></div>
            {pepPreview.soapNote ? <div className="space-y-3">{[['Anamnese', pepPreview.soapNote.anamnese], ['Subjetivo', pepPreview.soapNote.subjetivo], ['Objetivo', pepPreview.soapNote.objetivo], ['Avaliação', pepPreview.soapNote.avaliacao], ['Plano', pepPreview.soapNote.plano]].map(([label, value]) => <div key={label} className="rounded-md border p-3"><div className="mb-1 text-sm font-semibold">{label}</div><p className="whitespace-pre-wrap text-sm text-muted-foreground">{value || 'Não informado.'}</p></div>)}</div> : <p className="text-sm text-muted-foreground">Nenhuma evolução SOAP foi registrada nesta consulta.</p>}
          </div>
        </CenteredPreview>
      )}
    </>
  );
}

export function PatientExamsPageContent({ cdPaciente }: { cdPaciente: number }) {
  const { data, isLoading } = useExamesImagem(cdPaciente);
  const [laudoPreview, setLaudoPreview] = useState<ExameImagem | null>(null);
  const [dicomLoading, setDicomLoading] = useState<number | null>(null);

  async function openDicomWeb(exame: ExameImagem) {
    setDicomLoading(exame.cdExame);
    try {
      const result = await api.get<{ url: string }>(`/api/exames-imagem/${exame.cdExame}/dicom-web`);
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Não foi possível abrir o DICOM web.');
    } finally {
      setDicomLoading(null);
    }
  }

  return (
    <>
      <ClinicalShell title="Exames e laudos" eyebrow="Paciente" action={<Button><Plus className="mr-2 h-4 w-4" />Solicitar exame</Button>}>
        {isLoading && <LoadingState />}
        {!isLoading && (data ?? []).length === 0 && <p className="text-sm text-muted-foreground">Nenhum exame/laudo encontrado.</p>}
        <div className="mb-3 flex justify-end"><BadgePill>{data?.length ?? 0} registros</BadgePill></div>
        <div className="relative max-h-[68vh] overflow-auto pl-10 pr-2 before:absolute before:left-[16px] before:top-3 before:h-[calc(100%-1.5rem)] before:w-px before:bg-[#d5ece9]">
          {(data ?? []).map((exame) => (
            <div key={exame.cdExame} className="group relative mb-3 rounded-2xl border border-[#d9e2eb] bg-white p-4 shadow-sm transition hover:border-primary/40 hover:shadow-md">
              <span className="absolute -left-[33px] top-6 flex h-7 w-7 items-center justify-center rounded-full bg-[#e4f5f2] ring-4 ring-background">
                <FileText className="h-3.5 w-3.5 text-[#0f8b83]" />
              </span>
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="min-w-0">
                  <div className="truncate text-base font-bold uppercase tracking-tight text-[#12213b]">{exame.procedimento ?? 'Exame'}</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {exame.data ?? '-'} · Controle {exame.nrControle ?? '-'} · {exame.modalidade ?? 'Exame'}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {exame.temLaudo ? (
                    <Button variant="outline" size="sm" className="border-[#d9e2eb] font-semibold" onClick={() => setLaudoPreview(exame)}>
                      <Eye className="mr-2 h-4 w-4" />Laudo
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" disabled className="border-amber-300 bg-amber-50 text-amber-700 opacity-100">
                      <Clock3 className="mr-2 h-4 w-4" />Aguardando laudo
                    </Button>
                  )}
                  {exame.temImagem && (
                    <>
                      <Button variant="outline" size="sm" className="border-[#8fe3d8] text-[#087d76]" onClick={() => openDicomWeb(exame)} disabled={dicomLoading === exame.cdExame}>
                        {dicomLoading === exame.cdExame ? <Clock3 className="mr-2 h-4 w-4 animate-pulse" /> : <ExternalLink className="mr-2 h-4 w-4" />}DICOM web
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openPacs(exame.pacsUrl, exame.nrControle)}>
                        <ImageIcon className="mr-2 h-4 w-4" />Imagem
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ClinicalShell>
      {laudoPreview && (
        <CenteredPreview
          title={laudoPreview.procedimento ?? 'Laudo'}
          subtitle={`${laudoPreview.data ?? '-'} · Controle ${laudoPreview.nrControle ?? '-'}`}
          action={<a className="inline-flex items-center rounded-md border border-[#8fe3d8] px-3 py-2 text-sm font-semibold text-[#087d76] hover:bg-[#eefaf8]" href={`${apiUrl}/api/exames-imagem/${laudoPreview.cdExame}/laudo.pdf`} target="_blank" rel="noreferrer" download>Baixar PDF</a>}
          onClose={() => setLaudoPreview(null)}
        >
          <iframe
            title="Laudo"
            src={`${apiUrl}/api/exames-imagem/${laudoPreview.cdExame}/laudo.pdf`}
            className="h-[68vh] w-full rounded-md border bg-muted/20"
          />
        </CenteredPreview>
      )}
    </>
  );
}

function BadgePill({ children }: { children: ReactNode }) {
  return <span className="rounded-full bg-[#eef6f5] px-3 py-1.5 text-sm font-semibold text-[#087d76]">{children}</span>;
}

function getConsultaDocs(atributos: ClinusAtributo[], consulta: ClinusAtendimento) {
  const consultaData = normalizarDataClinux(consulta.dtData);
  return atributos.filter((a) => {
    if (a.cd_valor <= 0) return false;
    // O endpoint de prontuário com sn_historico=true retorna atributos de todo o paciente.
    // Quando o Clinux informa a data do atributo, ela precisa coincidir com o atendimento;
    // caso contrário, um anexo de outra consulta pode ser selecionado por engano.
    const atributoData = normalizarDataClinux(a.dt_data ?? a.dt_criacao);
    if (atributoData && consultaData && atributoData !== consultaData) return false;
    // Registros sem data só podem ser usados quando não há data disponível no atendimento.
    if (!atributoData && consultaData) return false;
    const nome = (a.ds_atributo ?? '').toUpperCase();
    return classificarAtributo(a) === 'consulta' || nome.includes('ANAMNESE') || nome.includes('EVOLU') || nome.includes('CONSULTA');
  });
}

function normalizarDataClinux(value?: string | null) {
  if (!value) return null;
  const br = value.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return iso ? `${iso[1]}-${iso[2]}-${iso[3]}` : null;
}

function openPacs(pacsUrl?: string | null, nrControle?: number | null) {
  if (!nrControle && !pacsUrl) return;
  window.open(
    pacsUrl || `http://clinux.vidavg.com.br:9090/pacs/viewer.jnlp?accessionNumber=${nrControle}`,
    '_blank',
    'noopener,noreferrer',
  );
}

type PatientGeneratedDocument = {
  id: string;
  type: string;
  title: string;
  content: string;
  status: string;
  createdAt: string;
  createdBy?: { login?: string; ds_nome?: string } | null;
};

export function PatientDocumentsPageContent({ cdPaciente }: { cdPaciente: number }) {
  const [documents, setDocuments] = useState<PatientGeneratedDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<PatientGeneratedDocument | null>(null);
  const { data: paciente } = usePaciente(cdPaciente);

  useEffect(() => {
    setIsLoading(true);
    api.get<PatientGeneratedDocument[]>(`/api/generated-documents/patient/${cdPaciente}`)
      .then(setDocuments)
      .catch(() => setDocuments([]))
      .finally(() => setIsLoading(false));
  }, [cdPaciente]);

  return (
    <div>
      <ClinicalShell
        title="Documentos"
        eyebrow="Paciente"
      >
        {isLoading && <LoadingState />}
        {!isLoading && documents.length === 0 && <p className="text-sm text-muted-foreground">Nenhum documento gerado para este paciente.</p>}
        {!isLoading && documents.map((document) => (
          <DocumentRow
            key={document.id}
            title={document.title}
            meta={`${document.type} · ${formatarDataHora(document.createdAt)} · Médico: ${document.createdBy?.login || 'Não informado'}`}
            onOpen={() => setSelectedDocument(document)}
          />
        ))}
      </ClinicalShell>
      {selectedDocument && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-lg bg-background p-5 shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">Visualizar documento</h2>
              <Button variant="ghost" size="icon" onClick={() => setSelectedDocument(null)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="mt-4 rounded-md border-l-4 border-primary bg-muted p-4">
              <div className="font-bold">PEP-VIDA · {selectedDocument.title}</div>
              <div className="mt-1 text-sm">Paciente: {paciente?.ds_paciente ?? `Código ${cdPaciente}`}</div>
              <div className="text-xs text-muted-foreground">Código: {cdPaciente} · Médico: {selectedDocument.createdBy?.login || 'Não informado'} · Emissão: {formatarDataHora(selectedDocument.createdAt)}</div>
            </div>
            <div className="mt-4 max-h-[55vh] overflow-auto rounded-md border bg-background p-4">
              <div className="whitespace-pre-wrap text-sm leading-6">{selectedDocument.content}</div>
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={() => setSelectedDocument(null)}>Fechar</Button>
              <Button onClick={() => printGeneratedDocument(selectedDocument, paciente?.ds_paciente ?? `Paciente ${cdPaciente}`, cdPaciente)}>
                Imprimir / Baixar PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatarDataHora(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
}

function printGeneratedDocument(document: PatientGeneratedDocument, patientName: string, cdPaciente: number) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;
  const logoUrl = 'https://vida.vidavg.com.br/pep-vida/brand/logo-vida.png';
  const safeContent = document.content;
  win.document.write(`<html><head><title>${document.title}</title><style>@page{margin:8mm 10mm 18mm}*{box-sizing:border-box}html,body{margin:0;padding:0}body{font-family:Arial,sans-serif;color:#111;line-height:1.4}header{position:relative;min-height:29mm;border-bottom:1px solid #0f766e;padding:0 0 5px;margin:0 0 6mm}header .brand{display:flex;justify-content:space-between;align-items:flex-start;gap:14px}header .identity{padding-top:0}header img{display:block;width:125px;height:auto;max-height:20mm;object-fit:contain}h1{font-size:18px;margin:0 0 6px}h2{font-size:14px;margin:0 0 4px;color:#0f766e}.meta{font-size:10px;color:#555}.content{font-size:13px;padding-bottom:8mm}footer{position:fixed;left:0;right:0;bottom:0;height:13mm;border-top:1px solid #9ca3af;padding-top:3px;text-align:center;font-size:8.5px;line-height:1.25;color:#4b5563;background:#fff}footer strong{display:block;color:#0f766e}@media print{header{break-inside:avoid}footer{position:fixed;bottom:0}}</style></head><body><header><div class="brand"><div class="identity"><h1>PEP-VIDA | ${document.title}</h1><h2>${patientName}</h2><div class="meta">Paciente: ${cdPaciente} · Médico: ${document.createdBy?.login || 'Não informado'} · Emissão: ${formatarDataHora(document.createdAt)}</div></div><img src="${logoUrl}" alt="Vida Diagnóstico e Saúde"></div></header><div class="content">${safeContent}</div><footer><strong>Vida Centro de Diagnósticos Médico e Saúde</strong>RUA MAMED UNTAR, 749, CENTRO, VÁRZEA GRANDE - MT - CEP: 78110-315<br>Telefone: 65 3029-9700</footer></body></html>`);
  win.document.close();
  win.focus();
  win.print();
}

function ClinicalShell({
  title,
  eyebrow,
  action,
  children,
}: {
  title: string;
  eyebrow: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-primary">{eyebrow}</div>
            <CardTitle className="mt-1">{title}</CardTitle>
          </div>
          {action}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function DocumentRow({ title, meta, onOpen }: { title: string; meta: string; onOpen: () => void }) {
  return (
    <div className="mb-3 flex items-center justify-between rounded-lg border bg-background p-4">
      <div>
        <div className="font-semibold">{title}</div>
        <div className="mt-1 text-sm text-muted-foreground">{meta}</div>
      </div>
      <Button variant="outline" onClick={onOpen}>Abrir</Button>
    </div>
  );
}

function CenteredPreview({
  title,
  subtitle,
  children,
  action,
  onClose,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  action?: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
      <div className="max-h-[88vh] w-full max-w-4xl overflow-hidden rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between gap-3 border-b p-4">
          <div className="min-w-0">
            <div className="truncate font-semibold">{title}</div>
            {subtitle && <div className="mt-1 text-xs text-muted-foreground">{subtitle}</div>}
          </div>
          <div className="flex gap-2">
            {action}
            <Button variant="outline" size="sm" onClick={onClose}>
              <X className="mr-2 h-4 w-4" />Fechar
            </Button>
          </div>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
